import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig, loginRequest, silentRequest } from '../../config/msalConfig';
import { FormattedMessage, useIntl } from 'react-intl';

interface TokenValidatorProps {
	children: React.ReactNode;
}

export default function TokenValidator({ children }: TokenValidatorProps) {
	const [token, setToken] = useState<string>('');
	const [error, setError] = useState<string | null>(null);
	const [isOfficeEnvironment] = useState<boolean>(() => typeof Office !== 'undefined' && !!Office.auth);
	const [msalInstance] = useState<PublicClientApplication>(() => new PublicClientApplication(msalConfig));
	const [isMsalInitialized, setIsMsalInitialized] = useState<boolean>(false);
	const intl = useIntl();

	useEffect(() => {
		const initializeMsal = async () => {
			try {
				await msalInstance.initialize();
				// Default to using the first account if no active account is set
				if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
					msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
				}
				setIsMsalInitialized(true);
			} catch (error) {
				console.error('Failed to initialize MSAL:', error);
				setError('Failed to initialize authentication. Please refresh the page.');
			}
		};

		initializeMsal();

		// Add event callback for handling redirects
		const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
			if (event.eventType === EventType.LOGIN_SUCCESS) {
				const payload = event.payload as AuthenticationResult;
				setToken(payload.accessToken);
				setError(null);
			}
		});

		return () => {
			if (callbackId) {
				msalInstance.removeEventCallback(callbackId);
			}
		};
	}, [msalInstance]);

	const getToken = async () => {
		try {
			if (!isMsalInitialized) {
				setError(intl.formatMessage({ id: 'auth.errors.initFailed' }));
				return;
			}

			// In browser environment, try to get token using MSAL
			const account = msalInstance.getActiveAccount();

			if (!account) {
				await msalInstance.loginPopup(loginRequest);
				return;
			}

			try {
				const response = await msalInstance.acquireTokenSilent({
					...silentRequest,
					account: account,
				});

				setToken(response.accessToken);
				setError(null);
			} catch (error) {
				console.error('Silent token acquisition failed:', error);
				// If silent token acquisition fails, try interactive
				await msalInstance.acquireTokenPopup(loginRequest);
			}
		} catch (exception: any) {
			console.error('Authentication error:', exception);
			if (exception.code) {
				switch (exception.code) {
					case 13001:
						setError(intl.formatMessage({ id: 'auth.errors.officeNotSignedIn' }));
						break;
					case 13002:
						setError(intl.formatMessage({ id: 'auth.errors.consentRequired' }));
						break;
					case 13006:
						setError(intl.formatMessage({ id: 'auth.errors.officeWebIssues' }));
						break;
					case 13008:
						setError(intl.formatMessage({ id: 'auth.errors.officeBusy' }));
						break;
					case 13010:
						setError(intl.formatMessage({ id: 'auth.errors.browserConfig' }));
						break;
					default:
						setError(intl.formatMessage({ id: 'auth.errors.unknownError' }, { message: exception.message || 'Unknown error' }));
				}
			} else {
				setError(intl.formatMessage({ id: 'auth.errors.authFailed' }));
			}
			setToken('');
		}
	};

	useEffect(() => {
		if (isMsalInitialized) {
			getToken();
		}
	}, [isMsalInitialized]);

	if (!token) {
		return (
			<Box sx={{ textAlign: 'center' }}>
				<Typography variant='h6' color='error' gutterBottom>
					{error || <FormattedMessage id='auth.required' />}
				</Typography>
				<Button variant='contained' onClick={getToken} sx={{ mt: 2 }} disabled={!isMsalInitialized && !isOfficeEnvironment}>
					{!isMsalInitialized && !isOfficeEnvironment ? (
						<FormattedMessage id='auth.initializing' />
					) : (
						<FormattedMessage id='auth.signIn' />
					)}
				</Button>
			</Box>
		);
	}

	return <>{children}</>;
}
